import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { BrandUnderline } from '@/components/generals/brand-underline'
import { SplitWords } from '@/components/generals/split-words'

/**
 * Chapter two, told in type. "pretty" gets struck out and squeezed, then "results" replaces it,
 * stretches wide, and the camera dives into the stem of its "l" until the screen turns cyan.
 */
export const ManifestoSection: FC = (): ReactNode => {
    const ref = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const pairA = q('[data-pair="a"]')[0] as HTMLElement
        const pairB = q('[data-pair="b"]')[0] as HTMLElement
        const wordsA = q('[data-pair="a"] [data-word]')
        const wordsB = q('[data-pair="b"] [data-word]')
        const strike = q('[data-strike]')[0] as HTMLElement
        const pretty = q('[data-pretty]')[0] as HTMLElement
        const results = q('[data-results]')[0] as HTMLElement
        const resultsMask = q('[data-results-mask]')[0] as HTMLElement
        const underline = q('[data-pair="b"] [data-underline]')[0] as SVGPathElement
        const fadeBeforeZoom = q('[data-pair="b"] [data-fade-before-zoom]')
        const letter = q('[data-l]')[0] as HTMLElement
        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            section.setAttribute('data-pinned', '')

            gsap.set(section, { height: '260vh' })
            gsap.set(pairB, { position: 'absolute', left: 0, right: 0, top: 0, marginTop: 0, autoAlpha: 0 })
            gsap.set([...wordsA, ...wordsB], { yPercent: 115 })
            gsap.set(strike, { scaleX: 0 })
            gsap.set(results, { '--wdth': 62, scale: 1 })
            gsap.set(underline, { strokeDasharray: 1, strokeDashoffset: 1 })

            const zoom = { p: 0, target: 1 }

            const renderZoom = () => {
                if (zoom.p > 0) {
                    const x = letter.offsetLeft + letter.offsetWidth / 2
                    const y = letter.offsetTop + letter.offsetHeight * 0.55

                    zoom.target = (2.8 * Math.max(window.innerWidth, window.innerHeight)) / Math.max(letter.offsetWidth, 1)
                    gsap.set(results, { transformOrigin: `${x}px ${y}px` })
                }

                gsap.set(results, { scale: 1 + (zoom.target - 1) * zoom.p })
            }

            const timeline = gsap.timeline({
                defaults: { ease: 'none' },
                scrollTrigger: {
                    trigger: section,
                    start: 'top 95%',
                    end: 'bottom bottom',
                    scrub: 0.6
                }
            })

            timeline
                .to(wordsA, { yPercent: 0, duration: 0.1, stagger: 0.012, ease: 'power3.out' }, 0)
                .to(strike, { scaleX: 1, duration: 0.1, ease: 'power2.inOut' }, 0.17)
                .to(pretty, { '--wdth': 62, opacity: 0.3, duration: 0.1 }, 0.2)
                .to(pairA, { yPercent: -16, autoAlpha: 0, duration: 0.08, ease: 'power2.in' }, 0.34)
                .set(pairB, { autoAlpha: 1 }, 0.4)
                .to(wordsB, { yPercent: 0, duration: 0.1, stagger: 0.012, ease: 'power3.out' }, 0.4)
                .to(results, { '--wdth': 125, duration: 0.14, ease: 'power2.inOut' }, 0.5)
                .to(underline, { strokeDashoffset: 0, duration: 0.08 }, 0.6)
                .to(fadeBeforeZoom, { autoAlpha: 0, duration: 0.06 }, 0.68)
                .set(resultsMask, { overflow: 'visible' }, 0.71)
                .to(zoom, { p: 1, duration: 0.2, ease: 'power3.in', onUpdate: renderZoom }, 0.72)
                .set(section, { attr: { 'data-tone': 'signal', 'data-surface': 'light' } }, 0.9)
                .to({}, { duration: 0.1 }, 0.9)

            return () => section.removeAttribute('data-pinned')
        })

        return () => mm.revert()
    }, { scope: ref })

    return (
        <section
            ref={ref}
            id='manifesto'
            data-tone='ink'
            data-surface='dark'
            aria-label="Pretty versus results"
            className='relative bg-ink text-paper'
        >
            <div data-stage='' className='flex items-center overflow-clip px-(--gutter) py-[22vh]'>
                <div className='relative w-full'>
                    <p data-pair='a' className='display text-[clamp(2.8rem,8.4vw,10rem)] leading-[0.92] wdth-87'>
                        <span className='block'>
                            <SplitWords text="Some are in the" />
                        </span>

                        <span className='block'>
                            <span className='line-mask'>
                                <span data-word='' className='relative'>
                                    <span data-pretty='' className='inline-block text-haze'>pretty</span>

                                    <span
                                        data-strike=''
                                        aria-hidden='true'
                                        className='absolute -left-[4%] top-[50%] h-[0.085em] w-[108%] origin-left -rotate-3 bg-signal'
                                    />
                                </span>
                            </span>{' '}
                            <SplitWords text="website" />
                        </span>

                        <span className='block'>
                            <SplitWords text="business." />
                        </span>
                    </p>

                    <p data-pair='b' className='display mt-[14vh] text-[clamp(2.8rem,8.4vw,10rem)] leading-[0.92] wdth-87'>
                        <span data-fade-before-zoom='' className='block'>
                            <SplitWords text="We're in the" />
                        </span>

                        <span className='block'>
                            <span data-results-mask='' className='line-mask'>
                                <span data-word='' className='relative'>
                                    <span data-results='' className='relative inline-block text-signal will-change-transform'>
                                        resu<span data-l=''>l</span>ts
                                        <BrandUnderline className='-bottom-[0.05em]' />
                                    </span>
                                </span>
                            </span>
                        </span>

                        <span data-fade-before-zoom='' className='block'>
                            <SplitWords text="business." />
                        </span>
                    </p>
                </div>
            </div>
        </section>
    )
}
